// AUTH-HARDENING V2 H3 — Web memory-access + cross-tab auth coordinator.
//
// Target model (ADR-032, Owner-approved):
//   * refresh credential: HttpOnly cookie ONLY (__Host-realityai_web_refresh)
//     — never readable or writable by JS, never in localStorage, never
//     broadcast;
//   * access credential: JS memory only (module-level variable). Lost on
//     reload — bootstrap re-mints it via POST /api/auth/web/access (no
//     rotation);
//   * cross-tab coordination: Web Locks mutex + BroadcastChannel
//     'realityai:web:auth:v2' + a NON-SENSITIVE generation counter in
//     localStorage. The counter is not a credential; it lets waiting tabs
//     detect that a sibling already rotated and adopt instead of replaying
//     a refresh (which the backend would treat as reuse).
//
// MODEL A (binding): bootstrap/new-tab -> /access (no rotation);
// expired/invalid access (401) -> /refresh (rotation). Rotation therefore
// keeps happening during normal activity; reuse detection stays intact.
//
// Legacy localStorage keys (realityai_token / realityai_refresh) are
// removed once on migration — one-time cleanup, rollback impact = re-login.

import { ACCESS_KEY, REFRESH_KEY } from './auth-keys';

const LOCK_NAME = 'realityai:web:refresh:v2';
const CHANNEL_NAME = 'realityai:web:auth:v2';
const GEN_KEY = 'realityai_auth_gen';

// ---------- memory access store (the ONLY place the access JWT lives) ----

let memoryAccess: string | null = null;

export function getMemoryAccess(): string | null {
  return memoryAccess;
}

export function setMemoryAccess(token: string | null): void {
  memoryAccess = token;
}

// ---------- generation counter (non-sensitive coordination state) -------

export function readGeneration(): number {
  try {
    return Number(localStorage.getItem(GEN_KEY) || '0');
  } catch {
    return 0;
  }
}

export function bumpGeneration(): number {
  const next = readGeneration() + 1;
  try {
    localStorage.setItem(GEN_KEY, String(next));
  } catch {
    /* coordination is best-effort without storage */
  }
  return next;
}

// ---------- v2 channel: ACCESS_UPDATED / LOGOUT (never refresh) ---------

export type AuthEventV2 =
  | { type: 'ACCESS_UPDATED'; access: string; generation: number }
  | { type: 'LOGOUT' };

let channel: BroadcastChannel | null = null;
try {
  if (typeof BroadcastChannel !== 'undefined') {
    channel = new BroadcastChannel(CHANNEL_NAME);
  }
} catch {
  channel = null;
}

// Last access observed from a sibling leader (this tab did not rotate).
let lastBroadcast: { access: string; generation: number } | null = null;

export function getBroadcastAccess(generation: number): string | null {
  if (lastBroadcast && lastBroadcast.generation > generation) {
    return lastBroadcast.access;
  }
  return null;
}

export function broadcastAuthEvent(ev: AuthEventV2): void {
  // BroadcastChannel delivers to sibling tabs only, never to the poster.
  channel?.postMessage(ev);
}

export function onAuthEventV2(handler: (ev: AuthEventV2) => void): () => void {
  const onBc = (e: MessageEvent) => {
    const d = e.data as AuthEventV2 | undefined;
    if (!d) return;
    if (d.type === 'ACCESS_UPDATED' && typeof d.access === 'string') {
      // Adopt the leader's access into THIS tab's memory immediately.
      lastBroadcast = { access: d.access, generation: d.generation };
      if (d.generation >= readGeneration()) {
        memoryAccess = d.access;
      }
      handler({ type: 'ACCESS_UPDATED', access: d.access, generation: d.generation });
    } else if (d.type === 'LOGOUT') {
      handler({ type: 'LOGOUT' });
    }
  };
  channel?.addEventListener('message', onBc);
  return () => {
    channel?.removeEventListener('message', onBc);
  };
}

// ---------- Web Locks mutex (same support baseline as QA-UX-001) --------

export async function withRefreshLockV2<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator !== 'undefined' && navigator.locks && navigator.locks.request) {
    return navigator.locks.request(LOCK_NAME, fn);
  }
  return fn();
}

// ---------- one-time legacy storage cleanup ------------------------------

export function purgeLegacyWebTokens(): void {
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* ignore */
  }
}
